<template>
    <transition>
        <ais-instant-search
            :search-client="searchClient"
            :index-name="productsIndex"
            :routing="routing"
        >
            <ais-configure :hits-per-page.camel="12" />

            <ais-current-refinements
                v-slot="{ items }"
                :included-attributes="['roam_products_categories.lvl0']"
            >
                {{ setEventsActive(items) }}
            </ais-current-refinements>

            <div class="mb-12 relative z-20">
                <div class="">
                    <div class="SearchBar max-w-4xl mx-auto px-5 lg:px-0">
                        <div class="flex justify-center lg:justify-between mb-8">
                            <ais-search-box
                                v-slot="{ currentRefinement, isSearchStalled, refine }"
                                class="h-full lg:w-1/2"
                            >
                                <div
                                    class="flex bg-white bg-opacity-25 h-16 overflow-hidden lg:h-20 blur"
                                >
                                    <input
                                        class="flex-grow w-32 ml-8 mr-4 h5 text-white bg-transparent outline-none placeholder-white placeholder-uppercase sm:w-auto"
                                        type="search"
                                        :value="currentRefinement"
                                        placeholder="Search for…"
                                        @input="refine($event.currentTarget.value)"
                                    />

                                    <img
                                        :hidden="!isSearchStalled"
                                        class="h-full"
                                        src="/themes/coralcoast/images/spinner.svg"
                                        alt=""
                                    />

                                    <span
                                        class="flex items-center justify-center w-16 border-l border-white border-opacity-25 lg:w-20 text-white"
                                    >
                                        <!-- prettier-ignore -->
                                        <svg class="w-4 h-4 stroke-current" viewBox="0 0 20 20">
                                            <g fill="none" fill-rule="evenodd" stroke-width="1.5" transform="translate(2 2)">
                                                <path stroke-linecap="round" d="M16 16L11 11"/>
                                                <circle cx="6.5" cy="6.5" r="6.5"/>
                                            </g>
                                        </svg>
                                    </span>
                                </div>
                            </ais-search-box>

                            <div class="hidden h-20 w-64 ml-4 lg:block blur">
                                <multiselect
                                    v-model="selectedIndex"
                                    :options="switcherOptions"
                                    :searchable="false"
                                    :close-on-select="true"
                                    :show-labels="false"
                                    placeholder="Listings"
                                    @input="indexSelected(selectedIndex)"
                                >
                                    <template slot="singleLabel" slot-scope="{ option }">
                                        {{ option.text }}
                                    </template>
                                    <template slot="option" slot-scope="{ option }">
                                        {{ option.text }}
                                    </template>
                                </multiselect>
                            </div>

                            <ais-menu-select
                                v-slot="{ items, canRefine, refine }"
                                attribute="roam_products_regions.lvl0"
                                class="hidden h-16 lg:h-20 w-64 ml-4 md:block blur"
                            >
                                <Select
                                    class="bg-white bg-opacity-25"
                                    :items="items"
                                    :can-refine="canRefine"
                                    :refine="refine"
                                    placeholder="All Regions"
                                />
                            </ais-menu-select>

                            <button
                                class="js-toggle search-toggle bg-white bg-opacity-25 blur hover:border hover:border-opacity-25 rounded-full w-16 h-16 flex justify-center items-center flex-shrink-0 focus:outline-none lg:hidden ml-4"
                                data-target="html"
                                data-target-class="ais-Search-categories-active"
                                @click="toggle"
                            >
                                <!-- prettier-ignore -->
                                <svg class="ais-Filter-open" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.826 9.29h.75a.75.75 0 0 0-.176-.483l-.574.483Zm-5.59-6.646.575-.483-.574.483Zm15.236 0-.574-.483.574.483Zm-5.646 6.713-.574-.483a.75.75 0 0 0-.176.483h.75Zm0 4.671.45.6a.75.75 0 0 0 .3-.6h-.75Zm-4 3h-.75a.75.75 0 0 0 1.2.6l-.45-.6ZM7.4 8.807 1.81 2.16l-1.147.965 5.589 6.646L7.4 8.807ZM1.81 2.16a.25.25 0 0 1 .192-.411V.25C.514.25-.295 1.988.662 3.126l1.149-.965Zm.192-.411h13.705V.25H2.002v1.5Zm13.705 0a.25.25 0 0 1 .19.41l1.149.966c.957-1.138.148-2.876-1.34-2.876v1.5Zm.19.41-5.645 6.714 1.148.966 5.646-6.714-1.148-.965Zm-5.821 7.197v4.671h1.5V9.357h-1.5Zm.3 4.071-4 3 .9 1.2 4-3-.9-1.2Zm-2.8 3.6V9.29h-1.5v7.738h1.5Z" fill="#FFFFFF"/></svg>
                                <!-- prettier-ignore -->
                                <svg class="ais-Filter-close hidden" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.9229 12.9234L5.07612 5.07661" stroke="#FFFFFF" stroke-linecap="square"/><path d="M12.9234 5.07616L5.07661 12.9229" stroke="#FFFFFF" stroke-linecap="square"/></svg>
                            </button>
                        </div>
                    </div>
                    <Categories />
                    <Dates
                        v-if="isEventsActive"
                        attribute="roam_products_eventDates"
                        class="py-6"
                    />
                </div>
            </div>

            <div class="searchResults relative container mb-12 sm:mb-48">
                <div class="max-w-screen-lg mx-auto relative z-10">
                    <ais-hits v-slot="{ items }" class="grid gap-5 grid-square pb-24">
                        <Product v-for="item in items" :key="item.objectID" :product="item" />
                    </ais-hits>

                    <ais-pagination
                        v-slot="{
                            currentRefinement,
                            pages,
                            isFirstPage,
                            isLastPage,
                            refine,
                            createURL
                        }"
                        :padding="2"
                    >
                        <div class="flex justify-center mt-16 small">
                            <a
                                v-if="!isFirstPage"
                                class="flex items-center justify-center w-12 h-12"
                                :href="createURL(currentRefinement - 1)"
                                @click.prevent="refine(currentRefinement - 1)"
                            >
                                <svg class="w-2 h-4 stroke-current" viewBox="0 0 6 10">
                                    <path
                                        fill="none"
                                        fill-rule="evenodd"
                                        stroke-linecap="round"
                                        stroke-width="1.5"
                                        d="M4.931 1.069L1.069 4.931m0 .138l3.862 3.862"
                                    />
                                </svg>
                            </a>

                            <a
                                v-for="page in pages"
                                :key="page"
                                class="flex items-center justify-center w-12 md:text-lg h-12 mx-1 hover:bg-blue-500 hover:text-white rounded-full"
                                :class="{
                                    'text-white bg-blue-500 rounded-full':
                                        page === currentRefinement
                                }"
                                :href="createURL(page)"
                                @click.prevent="refine(page)"
                            >
                                {{ page + 1 }}
                            </a>

                            <a
                                v-if="!isLastPage"
                                class="flex items-center justify-center w-12 h-12"
                                :href="createURL(currentRefinement + 1)"
                                @click.prevent="refine(currentRefinement + 1)"
                            >
                                <svg class="w-2 h-4 stroke-current" viewBox="0 0 6 10">
                                    <path
                                        fill="none"
                                        fill-rule="evenodd"
                                        stroke-linecap="round"
                                        stroke-width="1.5"
                                        d="M1.069 8.931l3.862-3.862m0-.138L1.069 1.069"
                                    />
                                </svg>
                            </a>
                        </div>
                    </ais-pagination>
                </div>
            </div>
        </ais-instant-search>
    </transition>
</template>

<script>
import algoliasearch from 'algoliasearch/lite';
import { routing } from '@/routing';
import Product from '@/components/Product.vue';
import Categories from '@/components/Categories.vue';
import Dates from '@/components/Dates.vue';
import Select from '@/components/Select.vue';
import Multiselect from 'vue-multiselect';

export class Toggle {
    constructor({
        source,
        target = document.querySelector(source.dataset.target),
        sourceClass = source.dataset.sourceClass,
        targetClass = source.dataset.targetClass
    }) {
        this.source = source;
        this.target = target;
        this.sourceClass = sourceClass;
        this.targetClass = targetClass;

        if (!this.source) return;

        this.source.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggleClasses();
        });
    }

    toggleClasses() {
        if (this.sourceClass) this.source.classList.toggle(this.sourceClass);
        if (this.targetClass && this.target) this.target.classList.toggle(this.targetClass);
    }
}

export default {
    components: {
        Product,
        Categories,
        Dates,
        Select,
        Multiselect
    },

    data() {
        return {
            searchClient: algoliasearch(window.algolia.id, window.algolia.key),
            routing: routing(`products_${this.$root.hub}`),
            selectedIndex: { value: `products_${this.$root.hub}`, text: 'Listings' },
            switcherOptions: [
                { value: `products_${this.$root.hub}`, text: 'Listings' },
                { value: `pages_${this.$root.hub}`, text: 'Pages' }
            ],
            isEventsActive: false
        };
    },

    computed: {
        productsIndex() {
            return this.isEventsActive
                ? `products_${this.$root.hub}_events`
                : `products_${this.$root.hub}`;
        }
    },

    methods: {
        setEventsActive(refinements) {
            const eventPrefix = 'Events';

            this.isEventsActive = refinements.some(
                (refinement) =>
                    refinement.attribute === 'roam_products_categories.lvl0' &&
                    refinement.refinements.some(
                        (cat) =>
                            cat.value === eventPrefix || cat.value.startsWith(`${eventPrefix} >`)
                    )
            );
        },

        indexSelected(index) {
            this.$emit('switch', index.value);
        },

        toggle() {
            new Toggle({ source: document.querySelector('.search-toggle') });
        }
    }
};
</script>

<style scoped>
.subitem-wrapper {
    &::before,
    &::after {
        margin: auto;
        content: '';
    }
}
</style>
